import {
	AssetWorkspaceTab,
	type AssetWorkspaceTabId,
} from "@/features/jobs/lib/get-asset-workspace-tab-id.util";

const ORG_ASSET_WORKSPACE_TAB_IDS = new Set<string>(
	Object.values(AssetWorkspaceTab),
);

/**
 * Builds the top-level org Asset detail drawer URL.
 */
export function getOrgAssetDrawerDetailsPath(input: {
	orgId: string;
	assetId: string;
	tab?: string;
}): string {
	const tab = input.tab ?? "details";
	return `/web/${input.orgId}/assets/${input.assetId}/${tab}`;
}

/**
 * Builds the top-level org Asset workspace URL.
 */
export function getOrgAssetWorkspacePath(input: {
	orgId: string;
	assetId: string;
	tab?: string;
}): string {
	const tab = input.tab ?? "form";
	return `/web/${input.orgId}/assets/${input.assetId}/workspace/${tab}`;
}

/**
 * Reads the org Asset workspace tab from a pathname.
 * Uses the first segment after `/assets/$assetId/workspace/`.
 */
export function getOrgAssetWorkspaceTabId(
	pathname: string,
): AssetWorkspaceTabId {
	const match = pathname.match(/\/assets\/[^/]+\/workspace(?:\/([^/]+))?/);
	if (!match) return AssetWorkspaceTab.Form;
	const segment = match[1] ?? "";
	if (ORG_ASSET_WORKSPACE_TAB_IDS.has(segment)) {
		return segment as AssetWorkspaceTabId;
	}
	return AssetWorkspaceTab.Form;
}

/**
 * Builds the nested Record workspace URL under an org Asset workspace.
 */
export function getOrgAssetScopedRecordWorkspacePath(input: {
	orgId: string;
	assetId: string;
	recordId: string;
	tab?: string;
}): string {
	const tab = input.tab ?? "form";
	return `/web/${input.orgId}/assets/${input.assetId}/workspace/records/${input.recordId}/workspace/${tab}`;
}

/**
 * Builds the Record detail drawer URL under an org Asset workspace.
 */
export function getOrgAssetScopedRecordDrawerDetailsPath(input: {
	orgId: string;
	assetId: string;
	recordId: string;
	tab?: string;
}): string {
	const tab = input.tab ?? "details";
	return `/web/${input.orgId}/assets/${input.assetId}/workspace/records/${input.recordId}/${tab}`;
}

/**
 * Builds the Asset detail drawer URL nested under an org Asset → Record
 * workspace Assets tab (`relatedAssetId` is the row asset; `assetId` is the host).
 */
export function getOrgAssetScopedRecordAssetDrawerDetailsPath(input: {
	orgId: string;
	assetId: string;
	recordId: string;
	relatedAssetId: string;
	tab?: string;
}): string {
	const tab = input.tab ?? "details";
	return `/web/${input.orgId}/assets/${input.assetId}/workspace/records/${input.recordId}/workspace/assets/${input.relatedAssetId}/${tab}`;
}

/**
 * Builds the Assets tab URL for a record nested under an org Asset workspace.
 */
export function getOrgAssetScopedRecordAssetsTabPath(input: {
	orgId: string;
	assetId: string;
	recordId: string;
}): string {
	return `/web/${input.orgId}/assets/${input.assetId}/workspace/records/${input.recordId}/workspace/assets`;
}

/**
 * True when the pathname is a Record workspace nested under an org Asset
 * workspace (`/web/$orgId/assets/$assetId/workspace/records/$recordId/workspace…`).
 *
 * Does not match job/ticket-nested paths (`…/workspace/assets/…`).
 */
export function isNestedRecordUnderOrgAssetWorkspacePath(
	pathname: string,
): boolean {
	return /\/web\/[^/]+\/assets\/[^/]+\/workspace\/records\/[^/]+\/workspace(?:\/|$)/.test(
		pathname,
	);
}
